import {
  ActivityData,
  ActivityLocation,
  CourseData,
  SubunitData,
  SubunitId,
  UnitData,
  UnitId,
} from 'course-types';

const CourseTraversalService = {
  getOrderedUnits(units: Record<UnitId, UnitData>): Array<UnitData & { id: UnitId }> {
    return Object.entries(units)
      .map((unitInfo) => ({ ...unitInfo[1], id: unitInfo[0] as UnitId }))
      .sort((a, b) => a.index - b.index);
  },

  getOrderedUnitContents(
    unit: UnitData | (UnitData & { id: UnitId }),
  ): Array<(SubunitData & { id: SubunitId }) | ActivityData> {
    return [
      ...Object.entries(unit.subunits).map((subInfo) => ({
        ...subInfo[1],
        id: subInfo[0] as SubunitId,
      })),
      ...(unit.activities ?? []),
    ].sort((a, b) => (a.index ?? -1) - (b.index ?? -1));
  },

  findUnitItemIndex(
    contents: Array<(SubunitData & { id: SubunitId }) | ActivityData>,
    subunitId: SubunitId | undefined,
    activityId: string,
  ): number {
    return subunitId
      ? contents.findIndex((c) => subunitId == (c.id as SubunitId) && !(c as ActivityData)?.type)
      : contents.findIndex((c) => activityId == c.id && (c as ActivityData)?.type);
  },

  getAdjacentActivities(
    courseData: CourseData,
    unitId: UnitId,
    subunitId: SubunitId | undefined,
    activityId: string,
  ): { previous: ActivityLocation | null; next: ActivityLocation | null } {
    const orderedUnits = this.getOrderedUnits(courseData.units);
    const location = orderedUnits.findIndex((unit) => unitId == (unit.id as UnitId));
    if (location == -1) return { previous: null, next: null };

    const precedingUnits = orderedUnits.slice(0, location + 1);
    const followingUnits = orderedUnits.slice(location);

    const orderedUnitContents = this.getOrderedUnitContents(followingUnits[0]);

    const previousActivity = this.findPreviousActivity(
      precedingUnits,
      orderedUnitContents,
      subunitId,
      activityId,
    );
    const nextActivity = this.findNextActivity(
      followingUnits,
      orderedUnitContents,
      subunitId,
      activityId,
    );

    return { previous: previousActivity, next: nextActivity };
  },

  findPreviousActivity(
    previousUnitsInclusive: Array<UnitData & { id: UnitId }>,
    unitContents: Array<(SubunitData & { id: SubunitId }) | ActivityData>,
    subunitId: SubunitId | undefined,
    activityId: string,
  ): ActivityLocation | null {
    // Loop through units backwards
    for (let i = previousUnitsInclusive.length - 1; i >= 0; i--) {
      const currentUnit = previousUnitsInclusive[i];
      const contents =
        i === previousUnitsInclusive.length - 1
          ? unitContents
          : this.getOrderedUnitContents(currentUnit);

      // Loop through unit contents backwards
      // Start at selected item if it is the initial unit the original activity belonged to
      for (
        let sub_i =
          i === previousUnitsInclusive.length - 1
            ? this.findUnitItemIndex(contents, subunitId, activityId)
            : contents.length - 1;
        sub_i >= 0;
        sub_i--
      ) {
        const item = contents[sub_i];

        // Return unit activity if it is not the original activity
        if ((item as ActivityData)?.type && item.id != activityId) {
          return { unitId: currentUnit.id, subunitId: undefined, activity: item as ActivityData };
        }

        // Else, process subunit
        if (!(item as ActivityData)?.type) {
          const subunitResult = this.findPreviousInSub(
            item as SubunitData & { id: SubunitId },
            currentUnit.id,
            subunitId,
            activityId,
          );
          // If defined, return activity
          if (subunitResult) {
            return subunitResult;
          }
        }
      }
    }
    return null;
  },

  findPreviousInSub(
    subunit: SubunitData & { id: SubunitId },
    unitId: UnitId,
    subunitId: SubunitId | undefined,
    activityId: string,
  ): ActivityLocation | undefined {
    // Start in middle of subunit if this was the original location
    const startPoint =
      subunit.id == subunitId
        ? (subunit?.activities ?? []).findIndex((a) => a.id == activityId) - 1
        : (subunit?.activities ?? []).length - 1;

    // Loop backwards through subunit activities
    for (let i = startPoint; i >= 0; i--) {
      const activity = subunit.activities[i];
      // Return subunit activity if it is not the original activity
      if (activity.id != activityId) {
        return { unitId: unitId, subunitId: subunit.id, activity: activity as ActivityData };
      }
    }
    return undefined;
  },

  findNextActivity(
    nextUnitsInclusive: Array<UnitData & { id: UnitId }>,
    unitContents: Array<(SubunitData & { id: SubunitId }) | ActivityData>,
    subunitId: SubunitId | undefined,
    activityId: string,
  ): ActivityLocation | null {
    // Loop through units
    for (let i = 0; i < nextUnitsInclusive.length; i++) {
      const currentUnit = nextUnitsInclusive[i];
      const contents = i === 0 ? unitContents : this.getOrderedUnitContents(currentUnit);

      // Loop through unit contents
      // Start at selected item if it is the initial unit the original activity belonged to
      for (
        let sub_i = i === 0 ? this.findUnitItemIndex(contents, subunitId, activityId) : 0;
        sub_i < contents.length;
        sub_i++
      ) {
        const item = contents[sub_i];

        // Return unit activity if it is not the original activity
        if ((item as ActivityData)?.type && item.id != activityId) {
          return { unitId: currentUnit.id, subunitId: undefined, activity: item as ActivityData };
        }

        // Else, process subunit
        if (!(item as ActivityData)?.type) {
          const subunitResult = this.findNextInSub(
            item as SubunitData & { id: SubunitId },
            currentUnit.id,
            subunitId,
            activityId,
          );
          // If defined, return activity
          if (subunitResult) {
            return subunitResult;
          }
        }
      }
    }
    return null;
  },

  findNextInSub(
    subunit: SubunitData & { id: SubunitId },
    unitId: UnitId,
    subunitId: SubunitId | undefined,
    activityId: string,
  ): ActivityLocation | undefined {
    // Start in middle of subunit if this was the original location
    const startPoint =
      subunit.id == subunitId
        ? (subunit?.activities ?? []).findIndex((a) => a.id == activityId) + 1
        : 0;
    if (startPoint < 0) return undefined;

    // Loop through subunit activities
    for (let i = startPoint; i < (subunit?.activities ?? []).length; i++) {
      const activity = subunit.activities[i];
      // Return subunit activity if it is not the original activity
      if (activity.id != activityId) {
        return { unitId: unitId, subunitId: subunit.id, activity: activity as ActivityData };
      }
    }
    return undefined;
  },

  getAllActivities(courseData: CourseData): ActivityData[] {
    const allActivities: ActivityData[] = [];
    const orderedUnits = this.getOrderedUnits(courseData.units);

    orderedUnits.forEach((unit) => {
      const orderedContents = this.getOrderedUnitContents(unit);

      orderedContents.forEach((content) => {
        if ((content as ActivityData)?.type) {
          // This is a unit-level activity
          allActivities.push(content as ActivityData);
        } else {
          // This is a subunit, get its activities
          const subunit = content as SubunitData & { id: SubunitId };
          if (subunit.activities) {
            // Sort subunit activities by index
            const sortedSubunitActivities = [...subunit.activities].sort(
              (a, b) => (a.index ?? -1) - (b.index ?? -1),
            );
            allActivities.push(...sortedSubunitActivities);
          }
        }
      });
    });

    return allActivities;
  },

  getCurrentActivityIndex(
    courseData: CourseData,
    activityId: string,
  ): { currentIndex: number; totalActivities: number } {
    const allActivities = this.getAllActivities(courseData);
    const currentIndex = allActivities.findIndex((activity) => activity.id === activityId);

    return {
      currentIndex: currentIndex >= 0 ? currentIndex + 1 : 0, // 1-based index
      totalActivities: allActivities.length,
    };
  },
};

export default CourseTraversalService;
